import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { PageBaseComponent } from '../../abstract/page-base/page-base.component';
import { CurrentUser } from '../../models/current-user';
import { UserOrganization } from '../../models/organization';
import { ApiService } from '../../services/api.service';
import { SanitizerService } from '../../services/sanitizer.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { Parameters } from '../../utilities/parameters';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MasterSelctabledetailComponent } from '../master-selctabledetail/master-selctabledetail.component';

@Component({
  selector: 'app-current-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ButtonGroupModule,
    CardModule,
    InputTextModule,
    PasswordModule,
    DividerModule,
    TabsModule,
    TableModule,
    SelectButtonModule,
    DialogModule,
    ToggleButtonModule,
    NgxExtendedPdfViewerModule,
    PageBaseComponent,
    TranslatePipe,
    MasterSelctabledetailComponent
  ],
  templateUrl: './current-user.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class CurrentUserComponent implements OnInit, OnDestroy {
  private messageService = inject(UiNotificationService);
  private translate = inject(TranslateService);
  private langSubscription!: Subscription

  user: CurrentUser;
  ruoli: string[];

  oldValue: string = '';
  newValue: string = '';
  chkValue: string = '';

  strongRegex: string = "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})";
  strongCheckRegExp: RegExp = new RegExp(this.strongRegex);

  expandedRows = {};
  tabsId: number = 0;

  stateOptions: any[] = [];

  showPrivacy: boolean = false;
  privacyPath: string = '';

  constructor(private apiService: ApiService, private sanitizer: SanitizerService) {
    this.user = { ...Parameters.loggedUser };
    this.ruoli = Parameters.descriviRuoli(this.user.roles);
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.tabsId = Parameters.confirmedConsents() ? 0 : 3;

    this.langSubscription = this.translate.stream(['no', 'yes']).subscribe(translations => {
      this.stateOptions = [
        { label: translations['no'], value: false },
        { label: translations['yes'], value: true }
      ];
    });
  }

  isValid(date: string | Date): boolean {
    const threshold = new Date(2000, 0, 1); // 01/01/2000
    const inputDate = new Date(date);
    return inputDate > threshold;

  }

  onCancel() {
    this.user = { ...Parameters.loggedUser };
  }

  onChange() {
    if (this.oldValue.trim().length == 0) {
      this.messageService.showErrorMessage('password.old');
      return;
    }

    if (!this.strongCheckRegExp.test(this.newValue)) {
      this.messageService.showErrorMessage('password.invalid');
      this.newValue = '';
      this.chkValue = '';

      return;
    }

    if (this.newValue != this.chkValue) {
      this.messageService.showErrorMessage('password.mismatch');
      this.newValue = '';
      this.chkValue = '';

      return;
    }

    this.apiService.changePassword(this.oldValue, this.newValue).subscribe({
      next: (status: boolean) => {
        if (status)
          this.messageService.showSuccess('password.saved');
        else
          this.messageService.showErrorMessage('messages.genericError');
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    });
  }

  onSave() {
    this.sanitizeUser();

    this.apiService.updateCurrentUser(this.user).subscribe({
      next: () => {
        Parameters.loggedUser = { ...this.user };
        this.messageService.showSuccess('messages.userUpdated');
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    });
  }

  onShowPrivacy(): void {
    const linguaCorrente = this.translate.currentLang();

    this.privacyPath = `./assets/${linguaCorrente}/privacy.pdf`;
    setTimeout(() => this.showPrivacy = true, 100);  }

  onRemoveOrganization(event: Event, org: UserOrganization): void {
    this.messageService.confirm(
      'messages.removeAssociation',
      () => { this.user.organizations = this.user.organizations.filter(o => o.organizationId != org.organizationId); },
      { labelKey: 'remove', severity: 'danger' }, 'confirm', 'pi-trash',
      { name: org.name }
     );
  }

  private sanitizeUser() {
    this.user.name = this.sanitizer.sanitizeData(this.user.name);
    this.user.familyName = this.sanitizer.sanitizeData(this.user.familyName);
    this.user.userName = this.sanitizer.sanitizeData(this.user.userName);
    this.user.email = this.sanitizer.sanitizeData(this.user.email);
  }
}
